import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  getUsers,
  deleteUser,
  uploadCompanyLogo,
  deleteCompanyLogo,
  assetUrl,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { adminConfig, useResetOnNavigate, ActionsWrap, KernelPage } from '../kernelAdminShared';
import { searchTerms, matchesAllTerms } from '../utils/kernelSearch';
import KernelSearchBox from '../components/KernelSearchBox';
import KernelHighlight from '../components/KernelHighlight';

const userStatusClass = (c) => {
  const active = Number(c.active_users) || 0;
  const max = Number(c.max_users) || 0;
  if (max > 0 && active > max) return 'users-link-high';
  if (max > 0 && active === max) return 'users-link-equal';
  return 'users-link-low';
};

const emptyForm = {
  name: '',
  description: '',
  business_sector: '',
  default_lang: 'it-IT',
  audit_enabled: '0',
  max_users: '1',
  end_date: '',
  start_date: '',
  street: '',
  zip: '',
  city: '',
  tax_id: '',
  pec: '',
  company_type: '',
  legal_name: '',
  legal_address: '',
  legal_zip: '',
  legal_city: '',
};

const BUSINESS_SECTORS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U'];

function CompanyManagement({ user, onLogout }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [publicLink, setPublicLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);

  const [logo, setLogo] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoError, setLogoError] = useState('');
  const [logoSuccess, setLogoSuccess] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoWide, setLogoWide] = useState(false);
  const [hideName, setHideName] = useState(false);

  const ALLOWED_LOGO_MIMES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];

  const todayISO = new Date().toISOString().slice(0, 10);

  const fetchCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCompanies();
      setCompanies(data.companies || []);
    } catch (err) {
      setError(errorMessage(err, t('companies.errorLoad')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  useResetOnNavigate(() => {
    setShowForm(false);
    setEditId(null);
  });

  const resetLogoState = () => {
    setLogo('');
    setLogoFile(null);
    setLogoPreview('');
    setLogoError('');
    setLogoSuccess('');
    setLogoWide(false);
    setHideName(false);
  };

  const startCreate = () => {
    resetMessages();
    setForm(emptyForm);
    setEditId(null);
    setCompanyUsers([]);
    resetLogoState();
    setShowForm(true);
  };

  const loadCompanyUsers = async (companyId) => {
    try {
      const data = await getUsers(false);
      setCompanyUsers((data.users || []).filter((u) => Number(u.company_id) === companyId));
    } catch {
      setCompanyUsers([]);
    }
  };

  const startEdit = (c) => {
    resetMessages();
    setForm({
      name: c.name || '',
      description: c.description || '',
      business_sector: c.business_sector || '',
      default_lang: c.default_lang || 'it-IT',
      audit_enabled: String(Number(c.audit_enabled) === 1 ? 1 : 0),
      max_users: String(c.max_users || '1'),
      end_date: c.end_date || '',
      start_date: c.start_date || '',
      street: c.street || '',
      zip: c.zip || '',
      city: c.city || '',
      tax_id: c.tax_id || '',
      pec: c.pec || '',
      company_type: c.company_type || '',
      legal_name: c.legal_name || '',
      legal_address: c.legal_address || '',
      legal_zip: c.legal_zip || '',
      legal_city: c.legal_city || '',
    });
    resetLogoState();
    setLogo(c.logo || '');
    setEditId(c.id);
    setShowForm(true);
    loadCompanyUsers(c.id);

    // Il token pubblico non è modificabile: lo si recupera (generandolo se
    // questa company non ne ha ancora uno) da un endpoint locale dedicato.
    if (adminConfig.companyPublicLink) {
      setPublicLink('');
      adminConfig.companyPublicLink
        .fetchToken(c.id)
        .then((res) => setPublicLink(adminConfig.companyPublicLink.buildUrl(res.public_token)))
        .catch(() => setPublicLink(''));
    }
  };

  const handleCopyPublicLink = async () => {
    // Si copia il link della pagina di anteprima (renderizzata dal backend con
    // i tag og:/twitter: corretti), non l'URL diretto del form React.
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard non disponibile: il link resta visibile e selezionabile a mano.
    }
  };

  const handleDeactivateUser = async (u) => {
    if (!window.confirm(t('companies.deactivateUserConfirm', { name: `${u.name} ${u.surname || ''}`.trim() }))) {
      return;
    }
    resetMessages();
    try {
      await deleteUser(u.id);
      await loadCompanyUsers(editId);
      fetchCompanies();
    } catch (err) {
      setError(errorMessage(err, t('companies.errorSave')));
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(t('companies.deleteConfirm', { name: c.name }))) {
      return;
    }
    resetMessages();
    try {
      await deleteCompany(c.id);
      setSuccess(t('companies.deleted'));
      fetchCompanies();
    } catch (err) {
      setError(errorMessage(err, t('companies.errorDelete')));
    }
  };

  const handleLogoChange = (e) => {
    setLogoError('');
    setLogoSuccess('');
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setLogoFile(null);
      setLogoPreview('');
      setLogoWide(false);
      setHideName(false);
      return;
    }
    const extOk = /\.(png|jpe?g|webp|gif|svg)$/i.test(file.name);
    if (!ALLOWED_LOGO_MIMES.includes(file.type) && !extOk) {
      setLogoError(t('settings.logoInvalidFormat'));
      e.target.value = '';
      setLogoFile(null);
      setLogoPreview('');
      setLogoWide(false);
      setHideName(false);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setLogoError(t('settings.logoTooLarge'));
      e.target.value = '';
      setLogoFile(null);
      setLogoPreview('');
      setLogoWide(false);
      setHideName(false);
      return;
    }
    const url = URL.createObjectURL(file);
    setLogoFile(file);
    setLogoPreview(url);
    setHideName(false);
    const img = new Image();
    img.onload = () => {
      const wide = img.naturalWidth > 0 && img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight > 1.4;
      setLogoWide(wide);
    };
    img.onerror = () => setLogoWide(false);
    img.src = url;
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !editId) return;
    setLogoError('');
    setLogoSuccess('');
    setLogoUploading(true);
    try {
      const res = await uploadCompanyLogo(editId, logoFile, hideName);
      const path = res?.company?.logo || '';
      setLogo(path);
      setLogoFile(null);
      setLogoPreview('');
      setLogoWide(false);
      setHideName(false);
      const input = document.getElementById('company-logo-file');
      if (input) input.value = '';
      setLogoSuccess(hideName ? t('settings.logoSavedHideName') : t('settings.logoSaved'));
      fetchCompanies();
    } catch (err) {
      setLogoError(errorMessage(err));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!editId || !window.confirm(t('settings.logoRemoveConfirm'))) return;
    setLogoError('');
    setLogoSuccess('');
    setLogoUploading(true);
    try {
      await deleteCompanyLogo(editId);
      setLogo('');
      setLogoFile(null);
      setLogoPreview('');
      setLogoSuccess(t('settings.logoRemoved'));
      fetchCompanies();
    } catch (err) {
      setLogoError(errorMessage(err));
    } finally {
      setLogoUploading(false);
    }
  };

  const overLimit = !!editId && Number(form.max_users) >= 1 && companyUsers.length > Number(form.max_users);

  const requiredEmpty = (v) => (v === '' || v == null ? ' required-empty' : '');

  const COMPANY_TYPE_OPTIONS = [
    { value: '', label: t('settings.companyTypeSelect') },
    { value: 'ditta_individuale', label: t('settings.companyTypeDittaIndividuale') },
    { value: 'libero_professionista', label: t('settings.companyTypeLiberoProfessionista') },
    { value: 'studio_associato', label: t('settings.companyTypeStudioAssociato') },
    { value: 'snc', label: t('settings.companyTypeSnc') },
    { value: 'sas', label: t('settings.companyTypeSas') },
    { value: 'srl', label: t('settings.companyTypeSrl') },
    { value: 'srls', label: t('settings.companyTypeSrls') },
    { value: 'spa', label: t('settings.companyTypeSpa') },
    { value: 'altro', label: t('settings.companyTypeAltro') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!form.name.trim()) {
      setError(t('companies.errorName'));
      return;
    }
    if (!form.start_date) {
      setError(t('companies.errorStartDate'));
      return;
    }
    if (Number(form.max_users) < 1) {
      setError(t('companies.errorMaxUsers'));
      return;
    }
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      setError(t('companies.errorEndBeforeStart'));
      return;
    }
    if (overLimit) {
      setError(t('companies.overLimitBlock'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        business_sector: form.business_sector || null,
        default_lang: form.default_lang,
        audit_enabled: Number(form.audit_enabled),
        max_users: Number(form.max_users),
        end_date: form.end_date || null,
        start_date: form.start_date || null,
        street: form.street.trim(),
        zip: form.zip.trim(),
        city: form.city.trim(),
        tax_id: form.tax_id.trim(),
        pec: form.pec.trim(),
        company_type: form.company_type,
        legal_name: form.legal_name.trim(),
        legal_address: form.legal_address.trim(),
        legal_zip: form.legal_zip.trim(),
        legal_city: form.legal_city.trim(),
      };
      if (editId) {
        await updateCompany(editId, payload);
        setSuccess(t('companies.updated'));
      } else {
        await createCompany(payload);
        setSuccess(t('companies.created'));
      }
      setShowForm(false);
      fetchCompanies();
    } catch (err) {
      setError(errorMessage(err, t('companies.errorSave')));
    } finally {
      setSaving(false);
    }
  };

  const isExpired = (c) => !!c.end_date && c.end_date < todayISO;

  // Le date sono in ISO (YYYY-MM-DD) nel DB e nei confronti sopra: qui le
  // riformatto solo per la visualizzazione, senza toccare il valore reale
  // (new Date() su una stringa ISO sposterebbe il giorno per fusi orari negativi).
  const fmtDateIT = (value) => {
    if (!value) return '—';
    const [y, m, d] = String(value).split('-');
    return y && m && d ? `${d}-${m}-${y}` : value;
  };

  const rowClass = (c) => {
    const classes = [];
    if (Number(c.status) === 4) classes.push('row-inactive');
    if (isExpired(c)) classes.push('row-expired');
    else if (!c.start_date || !c.end_date) classes.push('row-noend');
    return classes.join(' ');
  };

  // Ricerca: più parole separate da spazio, devono comparire tutte (AND), nel testo che si vede in tabella (non nelle date).
  const terms = searchTerms(search);
  const usersText = (c) => `${c.active_users ?? 0} / ${c.max_users || '∞'}`;
  const auditText = (c) => (Number(c.audit_enabled) === 1 ? t('companies.auditOn') : t('companies.auditOff'));
  const statusText = (c) => t(`companies.statusValues.${c.status}`) || String(c.status);
  const visibleCompanies = companies.filter((c) => matchesAllTerms(
    [c.name, Number(c.status) === 4 ? t('companies.inactive') : '', c.business_sector || '', usersText(c), auditText(c), statusText(c)].join(' '),
    terms
  ));

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('companies.title')}</h1>
          <p className="page-subtitle">{t('companies.subtitle')}</p>
        </div>
        {!showForm && (
          <button className="btn" type="button" onClick={startCreate}>
            {t('companies.newCompany')}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading && <div className="loading">{t('common.loading')}</div>}

      {!loading && !showForm && (
        <>
        <div className="ksb-bar">
          <KernelSearchBox value={search} onChange={setSearch} placeholder={t('companies.search')} title={t('companies.searchHint')} />
        </div>
        <div className="card">
          <div className="card-body table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('companies.name')}</th>
                  <th>{t('companies.sector')}</th>
                  <th>{t('companies.startDate')}</th>
                  <th>{t('companies.endDate')}</th>
                  <th>{t('companies.users')}</th>
                  <th>{t('companies.audit')}</th>
                  <th>{t('companies.status')}</th>
                  <th className="actions">{t('companies.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleCompanies.map((c) => (
                  <tr key={c.id} className={rowClass(c)}>
                    <td>
                      <KernelHighlight text={c.name} terms={terms} />
                      {Number(c.status) === 4 && (
                        <span className="badge badge-warning"><KernelHighlight text={t('companies.inactive')} terms={terms} /></span>
                      )}
                      {isExpired(c) && (
                        <span
                          className="warn-badge warn-badge-danger"
                          title={t('companies.expiredWarning', { date: fmtDateIT(c.end_date) })}
                        >
                          !
                        </span>
                      )}
                      {!isExpired(c) && !c.start_date && (
                        <span className="warn-badge warn-badge-warning" title={t('companies.noStartWarning')}>
                          !
                        </span>
                      )}
                      {!isExpired(c) && !c.end_date && (
                        <span className="warn-badge warn-badge-warning" title={t('companies.noEndWarning')}>
                          !
                        </span>
                      )}
                    </td>
                    <td>{c.business_sector ? <KernelHighlight text={c.business_sector} terms={terms} /> : '—'}</td>
                    <td>{fmtDateIT(c.start_date)}</td>
                    <td className={isExpired(c) ? 'text-danger' : ''}>{fmtDateIT(c.end_date)}</td>
                    <td className="link-cell">
                      <button
                        type="button"
                        className={`users-link ${userStatusClass(c)}`}
                        onClick={() => navigate(`/companies/${c.id}/users`)}
                        title={t('companies.viewUsers')}
                      >
                        <KernelHighlight text={usersText(c)} terms={terms} />
                      </button>
                    </td>
                    <td>
                      <span className={`badge ${Number(c.audit_enabled) === 1 ? 'badge-success' : 'badge-muted'}`}>
                        <KernelHighlight text={auditText(c)} terms={terms} />
                      </span>
                    </td>
                    <td><KernelHighlight text={statusText(c)} terms={terms} /></td>
                    <td className="actions">
                      <ActionsWrap>
                        <button
                          type="button"
                          className="icon-btn"
                          title={t('companies.edit')}
                          onClick={() => startEdit(c)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="icon-btn icon-btn-danger"
                          title={t('companies.delete')}
                          onClick={() => handleDelete(c)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </ActionsWrap>
                    </td>
                  </tr>
                ))}
                {visibleCompanies.length === 0 && (
                  <tr>
                    <td colSpan="8" className="empty-cell">
                      {companies.length === 0 ? t('companies.empty') : t('companies.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {!loading && showForm && (
        <div className="card">
          <div className="card-body">
            <h2 className="form-section-title">
              {editId ? t('companies.editTitle') : t('companies.newTitle')}
            </h2>

            {editId ? (
              <div style={{ marginBottom: 18 }}>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label>{t('settings.logo')}</label>
                </div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div
                    className="logo-upload-preview"
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 10,
                      border: '1px solid var(--border, #dfe3ec)',
                      background: 'var(--card-bg)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {logoPreview || assetUrl(logo) ? (
                      <img
                        src={logoPreview || assetUrl(logo)}
                        alt={t('settings.logoAlt')}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <span className="page-subtitle" style={{ fontSize: 11, textAlign: 'center', padding: 4 }}>
                        {t('settings.logoPlaceholder')}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <input
                      type="file"
                      id="company-logo-file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                      onChange={handleLogoChange}
                      style={{ marginBottom: 8, maxWidth: 420 }}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={handleLogoUpload}
                        disabled={!logoFile || logoUploading}
                      >
                        {logoUploading ? t('settings.logoUploading') : t('settings.logoUpload')}
                      </button>
                      {logo && (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={handleLogoDelete}
                          disabled={logoUploading}
                        >
                          {t('settings.logoRemove')}
                        </button>
                      )}
                    </div>
                    {logoWide && (
                      <label className="checkbox-row" style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', marginTop: 10 }}>
                        <input
                          type="checkbox"
                          checked={hideName}
                          onChange={(e) => setHideName(e.target.checked)}
                          style={{ marginTop: 0 }}
                        />
                        <span style={{ fontSize: 13 }}>{t('settings.logoHideNameLabel')}</span>
                      </label>
                    )}
                    {logoError && <div className="error-message" style={{ marginTop: 8 }}>{logoError}</div>}
                    {logoSuccess && <div className="success-message" style={{ marginTop: 8 }}>{logoSuccess}</div>}
                  </div>
                </div>
              </div>
            ) : (
              <p className="page-subtitle" style={{ marginTop: 0, marginBottom: 18 }}>
                {t('companies.logoAfterSave')}
              </p>
            )}

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-row-2">
                <div className={`form-group${requiredEmpty(form.name)}`}>
                  <label>{t('companies.name')} *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('companies.sector')}</label>
                  <select
                    value={form.business_sector}
                    onChange={(e) => setForm({ ...form, business_sector: e.target.value })}
                  >
                    <option value="">{t('companies.selectSector')}</option>
                    {BUSINESS_SECTORS.map((s) => (
                      <option key={s} value={s}>
                        {s} — {t(`companies.sectors.${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {adminConfig.companyPublicLink && editId && (
                <div className="form-group">
                  <label>{t('companies.publicLink')}</label>
                  <p className="page-subtitle" style={{ marginTop: 4, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>
                      {publicLink ? (
                        <>
                          {t('companies.publicLinkHint')} <strong>{publicLink}</strong>
                        </>
                      ) : (
                        t('common.loading')
                      )}
                    </span>
                    {publicLink && (
                      <button type="button" className="btn btn-sm btn-secondary" onClick={handleCopyPublicLink}>
                        {linkCopied ? t('companies.linkCopied') : t('companies.copyLink')}
                      </button>
                    )}
                  </p>
                </div>
              )}

              <div className="form-group">
                <label>{t('companies.description')}</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="form-row-2">
                <div className={`form-group${requiredEmpty(form.start_date)}`}>
                  <label>{t('companies.startDate')} *</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('companies.endDate')}</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className={`form-group${requiredEmpty(form.max_users)}`}>
                  <label>{t('companies.maxUsers')} *</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_users}
                    onChange={(e) => setForm({ ...form, max_users: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('companies.defaultLang')}</label>
                  <select
                    value={form.default_lang}
                    onChange={(e) => setForm({ ...form, default_lang: e.target.value })}
                  >
                    <option value="it-IT">{t('languages.it-IT')}</option>
                    <option value="en-GB">{t('languages.en-GB')}</option>
                  </select>
                </div>
              </div>

              <div className="form-row-check">
                <input
                  type="checkbox"
                  id="audit-enabled"
                  checked={Number(form.audit_enabled) === 1}
                  onChange={(e) => setForm({ ...form, audit_enabled: e.target.checked ? '1' : '0' })}
                />
                <label htmlFor="audit-enabled">{t('companies.auditLabel')}</label>
              </div>

              <h3 className="form-section-title" style={{ marginTop: 24 }}>{t('settings.agencyData')}</h3>
              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('settings.agencyStreet')}</label>
                  <input
                    type="text"
                    value={form.street}
                    onChange={(e) => setForm({ ...form, street: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.agencyCity')}</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('settings.agencyZip')}</label>
                  <input
                    type="text"
                    value={form.zip}
                    onChange={(e) => setForm({ ...form, zip: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.agencyPec')}</label>
                  <input
                    type="text"
                    value={form.pec}
                    onChange={(e) => setForm({ ...form, pec: e.target.value })}
                  />
                </div>
              </div>

              <h3 className="form-section-title" style={{ marginTop: 24 }}>{t('settings.billingData')}</h3>
              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('settings.agencyTaxId')}</label>
                  <input
                    type="text"
                    value={form.tax_id}
                    onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.agencyCompanyType')}</label>
                  <select
                    value={form.company_type}
                    onChange={(e) => setForm({ ...form, company_type: e.target.value })}
                  >
                    {COMPANY_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('settings.agencyLegalName')}</label>
                  <input
                    type="text"
                    value={form.legal_name}
                    onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.agencyLegalAddress')}</label>
                  <input
                    type="text"
                    value={form.legal_address}
                    onChange={(e) => setForm({ ...form, legal_address: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>{t('settings.agencyLegalZip')}</label>
                  <input
                    type="text"
                    value={form.legal_zip}
                    onChange={(e) => setForm({ ...form, legal_zip: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.agencyLegalCity')}</label>
                  <input
                    type="text"
                    value={form.legal_city}
                    onChange={(e) => setForm({ ...form, legal_city: e.target.value })}
                  />
                </div>
              </div>

              {overLimit && (
                <div className="form-warning">
                  <div className="form-warning-title">{t('companies.overLimitTitle')}</div>
                  <div>
                    {t('companies.overLimitMessage', {
                      count: companyUsers.length,
                      max: form.max_users,
                      diff: companyUsers.length - Number(form.max_users),
                    })}
                  </div>
                  <div className="overlimit-user-list">
                    {companyUsers.map((u) => (
                      <div key={u.id} className="overlimit-user-row">
                        <span>{u.name} {u.surname}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleDeactivateUser(u)}
                        >
                          {t('companyUsers.deactivate')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn" disabled={saving || overLimit}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </KernelPage>
  );
}

export default CompanyManagement;
